import React, { useRef, useEffect } from 'react'
import { graphql } from 'gatsby'
import Img from 'gatsby-image'
import styled from '@emotion/styled'
import { Parallax as ParallaxObject } from 'react-scroll-parallax'
import Swiper from '../../lib/swiper4'

import { getProject } from '../../projects'
import {
  ProjectLayout,
  ProjectIntro,
  Title,
  Paragraph,
  boxShadow,
  dropShadow,
} from '../../layouts/project'

import { useNavbarTheme } from '../../contexts/NavbarTheme'

import theme from '../../styles/theme'
import { media, min, rem, selection } from '../../styles/tools'

import { AutoFade, AutoSlide, AutoScale } from '../../components/Animated'
import Wrapper, { wrapperPadding } from '../../components/Wrapper'
import Space from '../../components/Space'
import Parallax from '../../components/Parallax'
import BackgroundText from '../../components/BackgroundText'
import WhenVisible from '../../components/WhenVisible'
import VideoPlayer from '../../components/VideoPlayer'
import BeforeAfterSlider from '../../components/BeforeAfterSlider'
import Link from '../../components/Link'
import { Headline, BoldText, Detail } from '../../styles/typography'

import Logo from '../../assets/pages/work/azubu/Logo.js'

import azubuE3Preview from '../../assets/pages/work/azubu/azubu-e3-preview.mp4'
import azubuE3 from '../../assets/pages/work/azubu/azubu-e3.mp4'
import e3Logo from '../../assets/pages/work/azubu/e3logo.svg'
import azubuLogoVideo from '../../assets/pages/work/azubu/azubu-logo.mp4'
import fortuneLogo from '../../assets/pages/work/azubu/fortune-logo.svg'
import polygonLogo from '../../assets/pages/work/azubu/polygon-logo.svg'
import storyboard1 from '../../assets/pages/work/azubu/storyboard1.gif'
import storyboard2 from '../../assets/pages/work/azubu/storyboard2.gif'
import teamImpulseHypePreviewVideo from '../../assets/pages/work/azubu/team-impulse-hype-preview.mp4'
import teamImpulseHypeVideo from '../../assets/pages/work/azubu/team-impulse-hype.mp4'
import ventureBeatLogo from '../../assets/pages/work/azubu/venture-beat-logo.svg'
import x from '../../assets/pages/work/azubu/x-close.svg'

const SliderContainer = styled('div')`
  width: calc(100vw - 80px);
  margin-left: auto;
  margin-right: auto;
  position: relative;
  /* Fix of Webkit flickering */
  z-index: 1;
  user-select: none;

  .swiper-wrapper {
    position: relative;
    z-index: 1;
    display: flex;
    transition-property: transform;
    box-sizing: content-box;
    will-change: transform;
  }

  .swiper-slide {
    flex-shrink: 0;
    width: 100%;
    position: relative;
    opacity: 0.3;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    transition: opacity 0.5s;
  }

  .swiper-slide.swiper-slide-active {
    opacity: 1;
  }

  .swiper-pagination {
    display: flex;
    justify-content: center;
    margin-top: 40px;
  }

  .swiper-pagination-bullet {
    width: 16px;
    height: 16px;
    display: block;
    border-radius: 50%;
    margin: 0 8px;
    box-shadow: inset 0 0 0 1px;
    transition: background-color 0.5s;
  }

  .swiper-pagination-bullet-active {
    background-color: #fff;
  }

  ${media.tablet} {
    width: 14.5vw;

    .swiper-pagination {
      display: none;
    }
  }
`

const IphoneSlider = ({ children, ...props }) => {
  const ref = useRef()

  useEffect(() => {
    const slider = new Swiper(ref.current, {
      initialSlide: 2,
      centeredSlides: true,
      grabCursor: true,
      spaceBetween: 20,
      pagination: '.swiper-pagination',
      paginationClickable: true,
    })

    return () => slider.destroy()
  }, [])

  return (
    <SliderContainer ref={ref} {...props}>
      <div className="swiper-wrapper">{children}</div>
      <div className="swiper-pagination" />
    </SliderContainer>
  )
}

export const query = graphql`
  {
    headerImage: file(relativePath: { eq: "pages/work/azubu.jpg" }) {
      ...windowWide
    }
    facebookImage: file(relativePath: { eq: "pages/work/azubu.jpg" }) {
      ...facebookShareImage
    }
    twitterImage: file(relativePath: { eq: "pages/work/azubu.jpg" }) {
      ...twitterShareImage
    }
    azubuBooth: file(relativePath: { eq: "pages/work/azubu/azubu-booth.png" }) {
      ...smallImage
    }
    azubuIcon: file(relativePath: { eq: "pages/work/azubu/azubu-icon.png" }) {
      ...smallImage
    }
    azubuLogo: file(relativePath: { eq: "pages/work/azubu/azubu-logo.png" }) {
      ...smallImage
    }
    cartoonFigure: file(
      relativePath: { eq: "pages/work/azubu/cartoon-figure.png" }
    ) {
      ...smallImage
    }
    device1: file(relativePath: { eq: "pages/work/azubu/device1.png" }) {
      ...smallImage
    }
    device2: file(relativePath: { eq: "pages/work/azubu/device2.png" }) {
      ...smallImage
    }
    device3: file(relativePath: { eq: "pages/work/azubu/device3.png" }) {
      ...smallImage
    }
    fakerPoster: file(
      relativePath: { eq: "pages/work/azubu/faker-poster.jpg" }
    ) {
      ...smallImage
    }
    gamescomLogo: file(
      relativePath: { eq: "pages/work/azubu/gamescom-logo.png" }
    ) {
      ...smallImage
    }
    gamescom3: file(relativePath: { eq: "pages/work/azubu/gamescom.jpg" }) {
      ...smallImage
    }
    gamescom1: file(relativePath: { eq: "pages/work/azubu/gamescom1.jpg" }) {
      ...smallImage
    }
    gamescom2: file(relativePath: { eq: "pages/work/azubu/gamescom2.jpg" }) {
      ...smallImage
    }
    gbmPoster: file(relativePath: { eq: "pages/work/azubu/gbm-poster.jpg" }) {
      ...smallImage
    }
    hoodie: file(relativePath: { eq: "pages/work/azubu/hoodie.png" }) {
      ...smallImage
    }
    iphone: file(relativePath: { eq: "pages/work/azubu/iphone.png" }) {
      ...smallImage
    }
    newLogo: file(relativePath: { eq: "pages/work/azubu/new-logo.png" }) {
      ...smallImage
    }
    oldLogo: file(relativePath: { eq: "pages/work/azubu/old-logo.png" }) {
      ...smallImage
    }
    parallax1: file(relativePath: { eq: "pages/work/azubu/parallax1.jpg" }) {
      ...windowWide
    }
    parallax2: file(relativePath: { eq: "pages/work/azubu/parallax2.jpg" }) {
      ...windowWide
    }
    parallax3: file(relativePath: { eq: "pages/work/azubu/parallax3.jpg" }) {
      ...windowWide
    }
    poster1: file(relativePath: { eq: "pages/work/azubu/poster1.png" }) {
      ...windowWide
    }
    poster2: file(relativePath: { eq: "pages/work/azubu/poster2.png" }) {
      ...windowWide
    }
    poster3: file(relativePath: { eq: "pages/work/azubu/poster3.png" }) {
      ...windowWide
    }
    poster4: file(relativePath: { eq: "pages/work/azubu/poster4.png" }) {
      ...windowWide
    }
    rokuIcon: file(relativePath: { eq: "pages/work/azubu/roku-icon.png" }) {
      ...windowWide
    }
    soda: file(relativePath: { eq: "pages/work/azubu/soda.png" }) {
      ...windowWide
    }
    storyboard3: file(
      relativePath: { eq: "pages/work/azubu/storyboard3.jpg" }
    ) {
      ...windowWide
    }
    storyboard4: file(
      relativePath: { eq: "pages/work/azubu/storyboard4.jpg" }
    ) {
      ...windowWide
    }
    team: file(relativePath: { eq: "pages/work/azubu/team.png" }) {
      ...windowWide
    }
    tvDisplay: file(relativePath: { eq: "pages/work/azubu/tv-display.png" }) {
      ...windowWide
    }
    webpage: file(relativePath: { eq: "pages/work/azubu/webpage.jpg" }) {
      ...windowWide
    }
    webpage1: file(relativePath: { eq: "pages/work/azubu/webpage1.jpg" }) {
      ...windowWide
    }
    webpage2: file(relativePath: { eq: "pages/work/azubu/webpage2.jpg" }) {
      ...windowWide
    }
    xpekePoster: file(
      relativePath: { eq: "pages/work/azubu/xpeke-poster.jpg" }
    ) {
      ...windowWide
    }
    screen1: file(relativePath: { eq: "pages/work/azubu/screen1.jpg" }) {
      ...windowWide
    }
    screen2: file(relativePath: { eq: "pages/work/azubu/screen2.jpg" }) {
      ...windowWide
    }
    screen3: file(relativePath: { eq: "pages/work/azubu/screen3.jpg" }) {
      ...windowWide
    }
    screen4: file(relativePath: { eq: "pages/work/azubu/screen4.jpg" }) {
      ...windowWide
    }
    screen5: file(relativePath: { eq: "pages/work/azubu/screen5.jpg" }) {
      ...windowWide
    }
    webpageBefore: file(
      relativePath: { eq: "pages/work/azubu/webpage-before.jpg" }
    ) {
      ...windowWide
    }
    webpageAfter: file(
      relativePath: { eq: "pages/work/azubu/webpage-after.jpg" }
    ) {
      ...windowWide
    }
    text1: file(relativePath: { eq: "pages/work/azubu/text1.png" }) {
      ...windowWide
    }
    text2: file(relativePath: { eq: "pages/work/azubu/text2.png" }) {
      ...windowWide
    }
    text3: file(relativePath: { eq: "pages/work/azubu/text3.png" }) {
      ...windowWide
    }
  }
`

let project = getProject('azubu')

export default ({ data }) => {
  project = {
    ...project,
    headerImage: data.headerImage.childImageSharp.fluid,
    facebookImage: data.facebookImage.childImageSharp.fixed.src,
    twitterImage: data.twitterImage.childImageSharp.fixed.src,
    logo: <Logo play delay={500} width={330} />,
  }

  const azubuBooth = data.azubuBooth.childImageSharp.fluid
  const azubuIcon = data.azubuIcon.childImageSharp.fluid
  const azubuLogo = data.azubuLogo.childImageSharp.fluid
  const cartoonFigure = data.cartoonFigure.childImageSharp.fluid
  const device1 = data.device1.childImageSharp.fluid
  const device2 = data.device2.childImageSharp.fluid
  const device3 = data.device3.childImageSharp.fluid
  const fakerPoster = data.fakerPoster.childImageSharp.fluid
  const gamescomLogo = data.gamescomLogo.childImageSharp.fluid
  const gamescom3 = data.gamescom3.childImageSharp.fluid
  const gamescom1 = data.gamescom1.childImageSharp.fluid
  const gamescom2 = data.gamescom2.childImageSharp.fluid
  const gbmPoster = data.gbmPoster.childImageSharp.fluid
  const hoodie = data.hoodie.childImageSharp.fluid
  const iphone = data.iphone.childImageSharp.fluid
  const newLogo = data.newLogo.childImageSharp.fluid
  const oldLogo = data.oldLogo.childImageSharp.fluid
  const parallax1 = data.parallax1.childImageSharp.fluid
  const parallax2 = data.parallax2.childImageSharp.fluid
  const parallax3 = data.parallax3.childImageSharp.fluid
  const poster1 = data.poster1.childImageSharp.fluid
  const poster2 = data.poster2.childImageSharp.fluid
  const poster3 = data.poster3.childImageSharp.fluid
  const poster4 = data.poster4.childImageSharp.fluid
  const rokuIcon = data.rokuIcon.childImageSharp.fluid
  const soda = data.soda.childImageSharp.fluid
  const storyboard3 = data.storyboard3.childImageSharp.fluid
  const storyboard4 = data.storyboard4.childImageSharp.fluid
  const team = data.team.childImageSharp.fluid
  const tvDisplay = data.tvDisplay.childImageSharp.fluid
  const webpage = data.webpage.childImageSharp.fluid
  const webpage1 = data.webpage1.childImageSharp.fluid
  const webpage2 = data.webpage2.childImageSharp.fluid
  const xpekePoster = data.xpekePoster.childImageSharp.fluid
  const screen1 = data.screen1.childImageSharp.fluid
  const screen2 = data.screen2.childImageSharp.fluid
  const screen3 = data.screen3.childImageSharp.fluid
  const screen4 = data.screen4.childImageSharp.fluid
  const screen5 = data.screen5.childImageSharp.fluid
  const webpageBefore = data.webpageBefore.childImageSharp.fluid
  const webpageAfter = data.webpageAfter.childImageSharp.fluid
  const text1 = data.text1.childImageSharp.fluid
  const text2 = data.text2.childImageSharp.fluid
  const text3 = data.text3.childImageSharp.fluid

  useNavbarTheme('dark')

  return (
    <ProjectLayout project={project}>
      <ProjectIntro
        project={project}
        text={
          <AutoFade>
            <Detail css={{ opacity: 0.5 }}>CHALLENGE:</Detail>
            <Space y={16} />
            <Title as="h1">
              Turning a rising eSports platform into a popular global
              entertainment network that rivals the competition.
            </Title>
          </AutoFade>
        }
      >
        <Space y={{ mobile: 60, tablet: 100 }} />
        <div
          css={{
            width: '115%',
            position: 'relative',
            margin: 'auto',
            marginBottom: '-30vmin',
            marginLeft: '-9%',
          }}
        >
          <div
            css={{
              width: '82%',
              margin: 'auto',
              // ...dropShadow(),
            }}
          >
            <AutoSlide up>
              <Img fluid={device1} />
            </AutoSlide>
          </div>
          <div
            css={{
              width: '36%',
              position: 'absolute',
              top: '72.2%',
              right: '20%',
              // ...dropShadow(),
            }}
          >
            <AutoSlide left delay={1 * 200} translate={50}>
              <Img fluid={device2} />
            </AutoSlide>
          </div>
          <div
            css={{
              width: '19%',
              position: 'absolute',
              top: '40%',
              right: '5.6%',
              // ...dropShadow(),
            }}
          >
            <AutoSlide up delay={2 * 200}>
              <Img fluid={device3} />
            </AutoSlide>
          </div>
          <div
            css={{
              width: '30%',
              position: 'absolute',
              top: '29%',
              left: '-2.6%',
              // ...dropShadow(),
            }}
          >
            <ParallaxObject y={[-65, 35]}>
              <AutoFade>
                <Img fluid={cartoonFigure} />
              </AutoFade>
            </ParallaxObject>
          </div>
        </div>
      </ProjectIntro>
      <Wrapper padded css={{ minHeight: '100vh', overflow: 'hidden' }}>
        <Space y="40vmin" />
        <Space y={60} />
        <BackgroundText
          as="h4"
          css={{ [media.max.tablet]: { display: 'none' } }}
        >
          PRESS
        </BackgroundText>
        <AutoSlide left css={{ marginTop: '-7vw' }}>
          <Title
            as="blockquote"
            css={{ maxWidth: '17em', fontStyle: 'italic' }}
          >
            “This Startup Plans to Take on Twitch and YouTube in ESports”
          </Title>
          <Space y={30} />
          <div css={{ position: 'relative' }}>
            <img
              src={fortuneLogo}
              css={{ display: 'inline-block', width: 210, maxWidth: '50%' }}
              alt=""
            />
            <Img
              fluid={text1}
              alt=""
              css={{
                width: '100%',
                maxWidth: 445,
                position: 'absolute !important',
                top: '-171%',
                left: '3%',
                pointerEvents: 'none',
              }}
            />
          </div>
        </AutoSlide>

        <Space y={{ mobile: 60, tablet: 120 }} />
        <AutoSlide right style={{ textAlign: 'right' }}>
          <Title
            as="blockquote"
            css={{ maxWidth: '17em', fontStyle: 'italic' }}
          >
            “Azubu raises $59M in debt to create an esports rival to Twitch”
          </Title>
          <Space y={30} />
          <div css={{ position: 'relative' }}>
            <img
              src={ventureBeatLogo}
              css={{ display: 'inline-block', width: 230, maxWidth: '50%' }}
              alt=""
            />
            <Img
              fluid={text2}
              alt=""
              css={{
                width: '100%',
                maxWidth: 445,
                position: 'absolute !important',
                top: '-470%',
                right: '5%',
                pointerEvents: 'none',
              }}
            />
          </div>
        </AutoSlide>

        <Space y={{ mobile: 60, tablet: 120 }} />

        <AutoSlide left>
          <Title
            as="blockquote"
            css={{ maxWidth: '19em', fontStyle: 'italic' }}
          >
            “Azubu goes after Twitch by focusing on eSports and customer
            service”
          </Title>
          <Space md />
          <div css={{ position: 'relative' }}>
            <img
              src={polygonLogo}
              css={{ display: 'inline-block', width: 230, maxWidth: '50%' }}
              alt=""
            />
            <Img
              fluid={text3}
              alt=""
              css={{
                width: '100%',
                maxWidth: 445,
                position: 'absolute !important',
                top: '-50%',
                left: '-1%',
                pointerEvents: 'none',
              }}
            />
          </div>
        </AutoSlide>
        <Space y={{ mobile: 100, tablet: 200 }} />
      </Wrapper>
      <Parallax
        direction="both"
        distance={0.3}
        background={
          <Img fluid={parallax1} css={{ height: '130%', userSelect: 'none' }} />
        }
        css={{ [media.max.tabletMd]: { display: 'none' } }}
      >
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            height: '80vh',
            paddingLeft: '35%',
            [min(1200)]: {
              paddingLeft: '45%',
            },
          }}
        >
          <div
            css={{
              marginTop: '-2.4vmax',
              padding: '0 32px',
              [media.tabletMd]: { padding: 0 },
            }}
          >
            <BackgroundText
              as="h4"
              css={{
                [media.max.tablet]: { display: 'none' },
                color: 'rgba(0, 0, 0, .15) !important',
              }}
            >
              THE&nbsp;BRAND
            </BackgroundText>
            <AutoSlide up delay={200} css={{ marginTop: '-9vmax' }}>
              <Title as="blockquote" css={{ maxWidth: '15em' }}>
                We modernized Azubu's brand identity to reflect a change in the
                company's new vision and direction.
              </Title>
            </AutoSlide>
          </div>
        </div>
      </Parallax>
      <Wrapper
        css={{
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '13% 10%',
          margin: 'auto',
          [media.max.tablet]: {
            '> *': {
              width: '100%',
              margin: '8% 0 !important',
              flex: 'initial !important',
            },
          },
          [media.tablet]: {
            flexDirection: 'row',
          },
        }}
      >
        <AutoFade css={{ flex: 1, transform: 'scale(0.75)' }}>
          <Img fluid={newLogo} />
        </AutoFade>

        <AutoFade
          delay={1 * 150}
          css={{
            [media.max.tablet]: { display: 'none' },
            flex: 0.3,
            margin: '0 7%',
          }}
        >
          <svg viewBox="0 0 94.728 21.309" fill="#bbb">
            <path d="M94.605,10.99c0.028-0.036,0.053-0.073,0.071-0.113c0.012-0.026,0.021-0.051,0.029-0.079c0.009-0.031,0.013-0.061,0.016-0.091c0.003-0.032,0.008-0.062,0.006-0.093c-0.002-0.023-0.01-0.045-0.015-0.069c-0.013-0.061-0.036-0.119-0.07-0.173c-0.016-0.025-0.031-0.05-0.05-0.071c-0.009-0.011-0.014-0.025-0.025-0.035L84.095,0.149c-0.214-0.203-0.554-0.198-0.758,0.015c-0.203,0.214-0.199,0.554,0.015,0.758l9.524,9.196H0.534C0.239,10.118,0,10.357,0,10.652c0,0.296,0.239,0.535,0.534,0.54h92.335l-9.517,9.196c-0.214,0.203-0.219,0.544-0.015,0.758c0.107,0.107,0.244,0.163,0.387,0.163c0.132,0,0.269-0.051,0.371-0.153l10.473-10.117c0.003-0.003,0.005-0.007,0.008-0.009c0.003-0.003,0.005-0.003,0.008-0.006C94.593,11.014,94.597,11.001,94.605,10.99" />
          </svg>
        </AutoFade>

        <AutoFade delay={2 * 150} css={{ flex: 1, transform: 'scale(0.75)' }}>
          <Img fluid={oldLogo} />
        </AutoFade>
      </Wrapper>

      <Wrapper padded css={{ [media.max.tabletMd]: { display: 'none' } }}>
        <AutoSlide up css={{ boxShadow: '0 20px 50px rgba(0, 0, 0, .3)' }}>
          <VideoPlayer
            webroll={azubuLogoVideo}
            video={azubuLogoVideo}
            ratio={1080 / 1920}
          />
        </AutoSlide>

        <Space y={{ mobile: 60, tablet: 120 }} />

        <WhenVisible>
          {inView => (
            <div
              css={{
                display: 'flex',
                justifyContent: 'center',
                position: 'relative',
                transition: '1s ease-out',
                opacity: inView ? 1 : 0,
                transform: inView ? 'none' : 'translateY(80px)',
              }}
            >
              <div
                css={{
                  width: '55%',
                  position: 'relative',
                  zIndex: 2,
                  ...boxShadow(0.9, '0 7px 70px'),
                }}
              >
                <Img fluid={fakerPoster} />
              </div>
              <div
                css={{
                  width: '50%',
                  position: 'absolute',
                  zIndex: 1,
                  left: '-10%',
                  top: '12%',
                  transition: '1s .5s ease-out',
                  transform: inView ? 'none' : 'translateX(70%)',
                }}
              >
                <Img fluid={gbmPoster} />
              </div>
              <div
                css={{
                  width: '50%',
                  position: 'absolute',
                  zIndex: 1,
                  right: '-10%',
                  top: '12%',
                  transition: '1s .5s ease-out',
                  transform: inView ? 'none' : 'translateX(-70%)',
                }}
              >
                <Img fluid={xpekePoster} />
              </div>
            </div>
          )}
        </WhenVisible>

        <Space y={{ mobile: 80, tablet: 180 }} />
      </Wrapper>

      <Wrapper>
        <div
          css={{
            display: 'flex',
            width: '90%',
            margin: 'auto',
            '> *': { flex: 1 },
          }}
        >
          <AutoSlide down delay={2 * 300}>
            <Img fluid={poster1} />
          </AutoSlide>
          <AutoSlide down delay={3 * 300}>
            <Img fluid={poster2} />
          </AutoSlide>
          <AutoSlide down delay={4 * 300}>
            <Img fluid={poster3} />
          </AutoSlide>
          <AutoSlide down delay={5 * 300}>
            <Img fluid={poster4} />
          </AutoSlide>
        </div>
        <Space y={60} />
      </Wrapper>
      <Wrapper padded>
        <BackgroundText
          as="h4"
          css={{ [media.max.tablet]: { display: 'none' } }}
        >
          EVENTS
        </BackgroundText>
        <div css={{ height: '47vw', position: 'relative', marginTop: '-7vw' }}>
          <AutoFade
            delay={0 * 150}
            css={{
              width: '53%',
              position: 'absolute',
              zIndex: 2,
              top: '0%',
              left: '0%',
              ...boxShadow(),
            }}
          >
            <Img fluid={gamescom1} />
          </AutoFade>
          <AutoFade
            delay={1 * 150}
            css={{
              width: '62%',
              position: 'absolute',
              zIndex: 1,
              top: '20%',
              right: '0%',
              ...boxShadow(),
            }}
          >
            <Img fluid={gamescom2} />
          </AutoFade>
          <AutoFade
            delay={2 * 150}
            css={{
              width: '53%',
              position: 'absolute',
              zIndex: 3,
              top: '43%',
              left: '6%',
              ...boxShadow(),
            }}
          >
            <Img fluid={gamescom3} />
          </AutoFade>
          <AutoFade
            delay={3 * 150}
            css={{
              width: '37vw',
              position: 'absolute',
              zIndex: 3,
              top: '83%',
              right: '0%',
              ...boxShadow(),
            }}
          >
            <Title as="p" css={{ fontSize: '2.63vw' }}>
              The new Azubu launched at two of the world's largest gaming
              events, Gamescom Europe and E3 in Los&nbsp;Angeles drawing nearly
              half a million fans.
            </Title>
          </AutoFade>
        </div>
        <Space y={{ mobile: 60, tabletMd: 120 }} />
        <Space y={{ mobile: 150, tabletMd: 300 }} />
      </Wrapper>

      <Wrapper
        css={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <AutoFade css={{ width: '26vw', [media.tabletMd]: { width: 200 } }}>
          <Img fluid={gamescomLogo} />
        </AutoFade>

        <AutoFade
          delay={1 * 200}
          css={{
            width: '6vw',
            margin: '-2vw 7vw 0',
            [media.tablet]: {
              width: 40,
              margin: '-10px 60px 0',
            },
          }}
        >
          <img src={x} />
        </AutoFade>
        <AutoFade
          delay={2 * 200}
          css={{
            width: '22vw',
            marginTop: -10,
            [media.tablet]: {
              width: 170,
            },
          }}
        >
          <Img fluid={azubuLogo} />
        </AutoFade>
      </Wrapper>
      <Wrapper padded>
        <Space y={{ mobile: 100, tabletMd: 200 }} />
        <AutoFade css={{ width: '80%' }}>
          <Img fluid={azubuBooth} />
        </AutoFade>
        <AutoFade css={{ width: '60%', marginTop: '-8%', marginLeft: '45%' }}>
          <Img fluid={hoodie} />
        </AutoFade>
        <AutoFade css={{ width: '60%', marginTop: '-15%', marginLeft: '6%' }}>
          <Img fluid={soda} />
        </AutoFade>
        <Space y={{ mobile: 100, tabletMd: 200 }} />
      </Wrapper>
      <Wrapper
        css={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <AutoFade css={{ width: '10vw', [media.tablet]: { width: 75 } }}>
          <img src={e3Logo} alt="" />
        </AutoFade>
        <AutoFade
          delay={1 * 200}
          css={{
            width: '6vw',
            margin: '-2vw 7vw 0',
            [media.tablet]: {
              width: 40,
              margin: '-10px 60px 0',
            },
          }}
        >
          <img src={x} />
        </AutoFade>
        <AutoFade
          delay={2 * 200}
          css={{
            width: '22vw',
            marginTop: -10,
            [media.tablet]: {
              width: 170,
            },
          }}
        >
          <Img fluid={azubuLogo} />
        </AutoFade>
      </Wrapper>
      <Wrapper padded>
        <Space y={{ mobile: 60, tabletMd: 120 }} />
        <AutoSlide up css={{ boxShadow: '0 20px 50px rgba(0, 0, 0, .3)' }}>
          <VideoPlayer
            webroll={azubuE3Preview}
            video={azubuE3}
            ratio={1080 / 1920}
          />
        </AutoSlide>
        <Space y={{ mobile: 60, tabletMd: 120 }} />
      </Wrapper>

      <Parallax
        direction="both"
        distance={0.3}
        background={
          <Img fluid={parallax2} css={{ height: '130%', userSelect: 'none' }} />
        }
      >
        <div
          css={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '80vh',
            maxHeight: 650,
            padding: '0 32px',
            [media.tabletMd]: { padding: 0 },
          }}
        >
          <Title
            as="blockquote"
            css={{ maxWidth: '18em', textAlign: 'center' }}
          >
            Our goal was to turn a best in-class streaming platform into a
            leading brand for competitive eSports.
          </Title>
        </div>
      </Parallax>

      <Wrapper padded>
        <Space y={{ mobile: 60, tablet: 120 }} />
        <BackgroundText
          as="h4"
          css={{ [media.max.tablet]: { display: 'none' } }}
        >
          DIGITAL
        </BackgroundText>
        <AutoSlide up css={{ marginTop: '-7vw' }}>
          <Img fluid={webpage} />
        </AutoSlide>
        <Space y={{ mobile: 60, tablet: 120 }} />
        <AutoSlide up css={{ display: 'flex', justifyContent: 'center' }}>
          <BeforeAfterSlider
            before={webpageBefore.src}
            after={webpageAfter.src}
            ratio={1566 / 2199}
          />
        </AutoSlide>
        <Space y={{ mobile: 60, tablet: 120 }} />
        <div css={{ position: 'relative' }}>
          <AutoSlide up css={{ width: '76%', position: 'relative' }}>
            <Img fluid={webpage1} />
          </AutoSlide>
          <AutoSlide
            up
            delay={100}
            css={{
              width: '76%',
              position: 'absolute',
              top: '29.7%',
              left: '24%',
              ...boxShadow(),
            }}
          >
            <Img fluid={webpage2} />
          </AutoSlide>
        </div>
        <Space y={{ mobile: 128, tabletMd: 200 }} />
      </Wrapper>
      <div
        css={{
          background: `${project.color} linear-gradient(transparent, #502f82 50%) center bottom / 100% 100vh no-repeat`,
        }}
      >
        <Space y={{ mobile: 128, tabletMd: 200 }} />
        <AutoFade>
          <Img
            fluid={azubuIcon}
            css={{
              width: 150,
              height: 150,
              margin: 'auto',
              borderRadius: '24%',
              ...boxShadow(),
            }}
          />
        </AutoFade>
        <AutoSlide up css={{ overflow: 'hidden' }}>
          <Space y={{ mobile: 128, tabletMd: 200 }} />
          <div css={{ position: 'relative' }}>
            <IphoneSlider>
              <div className="swiper-slide">
                <Img fluid={screen1} css={boxShadow()} />
              </div>
              <div className="swiper-slide">
                <Img fluid={screen2} css={boxShadow()} />
              </div>
              <div className="swiper-slide">
                <Img fluid={screen3} css={boxShadow()} />
              </div>
              <div className="swiper-slide">
                <Img fluid={screen4} css={boxShadow()} />
              </div>
              <div className="swiper-slide">
                <Img fluid={screen5} css={boxShadow()} />
              </div>
            </IphoneSlider>

            <div
              css={{
                [media.max.tablet]: { display: 'none' },
                width: '22.9vw',
                margin: 'auto',
                position: 'absolute',
                zIndex: 1,
                top: '-4.9vw',
                left: 'calc(50% - 11.2vw)',
                pointerEvents: 'none',
                userSelect: 'none',
              }}
            >
              <Img fluid={iphone} />
            </div>
          </div>
          <Space y={{ mobile: 128, tabletMd: 200 }} />
        </AutoSlide>
        <AutoFade>
          <Img
            fluid={rokuIcon}
            css={{
              width: 150,
              height: 150,
              margin: 'auto',
              borderRadius: '24%',
              ...boxShadow(),
            }}
          />
        </AutoFade>
        <Space y={{ mobile: 128, tabletMd: 100 }} />
        <AutoSlide
          up
          css={{
            width: '80%',
            [media.tabletMd]: { width: '66.666%' },
            margin: 'auto',
            marginBottom: '-50vmin',
            ...dropShadow(),
          }}
        >
          <Img fluid={tvDisplay} />
        </AutoSlide>
        <Space y={{ mobile: 128, tabletMd: 200 }} />
      </div>

      <Wrapper>
        <Space y="30vmin" />
        <Space y={{ mobile: 60, tabletMd: 120 }} />
        <Wrapper padded>
          <BackgroundText
            as="h4"
            css={{ [media.max.tablet]: { display: 'none' } }}
          >
            VIDEO
          </BackgroundText>
          <div
            css={{ height: '47vw', position: 'relative', marginTop: '-7vw' }}
          >
            <AutoFade
              delay={0 * 150}
              css={{
                width: '36vw',
                position: 'absolute',
                top: '1vw',
                left: '2vw',
                zIndex: 1,
                ...boxShadow(),
              }}
            >
              <img src={storyboard1} />
            </AutoFade>
            <AutoFade
              delay={1 * 150}
              css={{
                width: '36vw',
                position: 'absolute',
                top: '10.5vw',
                left: '32.6vw',
                zIndex: 2,
                ...boxShadow(),
              }}
            >
              <img src={storyboard2} />
            </AutoFade>
            <AutoFade
              delay={2 * 150}
              css={{
                width: '36vw',
                position: 'absolute',
                top: '18.3vw',
                left: '-5.5vw',
                ...boxShadow(),
              }}
            >
              <Img fluid={storyboard3} />
            </AutoFade>
            <AutoFade
              delay={3 * 150}
              css={{
                width: '36vw',
                position: 'absolute',
                top: '27vw',
                left: '20.7vw',
                ...boxShadow(),
              }}
            >
              <Img fluid={storyboard4} />
            </AutoFade>
          </div>
          <Space y={{ mobile: 60, tabletMd: 120 }} />
          <AutoSlide up css={{ boxShadow: '0 20px 50px rgba(0, 0, 0, .3)' }}>
            <VideoPlayer
              webroll={teamImpulseHypePreviewVideo}
              video={teamImpulseHypeVideo}
              ratio={1080 / 1920}
            />
          </AutoSlide>
          <Space y={{ mobile: 60, tabletMd: 120 }} />
          <AutoSlide up>
            <Img fluid={team} />
          </AutoSlide>
        </Wrapper>
      </Wrapper>

      <Parallax
        direction="both"
        distance={0.3}
        background={
          <Img fluid={parallax3} css={{ height: '130%', userSelect: 'none' }} />
        }
      >
        <div
          css={[
            wrapperPadding,
            selection(theme.colors.white, project.color),
            {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '80vh',
              maxHeight: rem(650),
              [media.laptop]: { minHeight: rem(600) },
              color: theme.colors.white,
            },
          ]}
        >
          <AutoSlide up translate={50}>
            <Title
              as="blockquote"
              css={{ maxWidth: '21em', textAlign: 'center' }}
            >
              Unwavering in their commitment to eSports Azubu acquired
              competitor Hitbox.tv in early 2017, gaining market share and
              legitimacy from the gaming community to take on rival Twitch.
            </Title>
          </AutoSlide>
        </div>
      </Parallax>
    </ProjectLayout>
  )
}
